// ===========================================
// Avatar API
// ===========================================
// API client for user avatar upload feature (Priority 11)
//
// IMPORTANT: This is for USER photo uploads (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

import { apiClient } from '../services/api';
import {
  UserAvatarInfo,
  AvatarSettings,
  GetAvatarResponse,
  UploadAvatarResponse,
  DeleteAvatarResponse,
  GetAvatarSettingsResponse,
  CanUploadResponse,
} from '../types/avatar.types';

const BASE_URL = '/avatar';

// ===========================================
// CURRENT USER AVATAR
// ===========================================

/**
 * Get current user's avatar info
 */
export async function getMyAvatar(): Promise<UserAvatarInfo> {
  const response = await apiClient.get<GetAvatarResponse>(BASE_URL);
  return response.data;
}

/**
 * Upload avatar for current user
 * @param file - Image file to upload (JPEG or PNG)
 */
export async function uploadMyAvatar(file: File): Promise<UserAvatarInfo> {
  const formData = new FormData();
  formData.append('avatar', file);

  const response = await apiClient.post<UploadAvatarResponse>(BASE_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Remove current user's avatar (reverts to initials)
 */
export async function removeMyAvatar(): Promise<UserAvatarInfo> {
  const response = await apiClient.delete<DeleteAvatarResponse>(BASE_URL);
  return response.data;
}

// ===========================================
// USER AVATAR (by userId)
// ===========================================

/**
 * Get avatar info for a specific user
 * @param userId - User ID
 * Access: Admin, Teacher
 */
export async function getUserAvatar(userId: string): Promise<UserAvatarInfo> {
  const response = await apiClient.get<GetAvatarResponse>(`${BASE_URL}/user/${userId}`);
  return response.data;
}

/**
 * Upload avatar for a specific user
 * @param userId - User ID
 * @param file - Image file to upload (JPEG or PNG)
 * Access: Admin only
 */
export async function uploadUserAvatar(userId: string, file: File): Promise<UserAvatarInfo> {
  const formData = new FormData();
  formData.append('avatar', file);

  const response = await apiClient.post<UploadAvatarResponse>(
    `${BASE_URL}/user/${userId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Remove avatar for a specific user
 * @param userId - User ID
 * Access: Admin only
 */
export async function removeUserAvatar(userId: string): Promise<UserAvatarInfo> {
  const response = await apiClient.delete<DeleteAvatarResponse>(`${BASE_URL}/user/${userId}`);
  return response.data;
}

// ===========================================
// AVATAR SETTINGS
// ===========================================

/**
 * Get avatar settings for the school
 * Access: Admin only
 */
export async function getAvatarSettings(): Promise<AvatarSettings> {
  const response = await apiClient.get<GetAvatarSettingsResponse>(`${BASE_URL}/settings`);
  return response.data;
}

/**
 * Check if current user can upload an avatar
 * Access: Any authenticated user
 */
export async function canUploadAvatar(): Promise<boolean> {
  const response = await apiClient.get<CanUploadResponse>(`${BASE_URL}/can-upload`);
  return response.data.canUpload;
}
